import { subject as caslSubject } from '@casl/ability';
import type { Action, Subject, UserRole } from '@/components/authnz/types/rbac';
import { defineAbilitiesFor } from './casl-abilities';
import type { CaslAppAbility } from './casl-types';
import { packRules } from './pack-rules';
import type {
  AccessReason,
  AccessReasonKind,
  Decision,
  DecisionJSON,
  HttpFailure,
} from './types';

function caslAbilityForDecision(json: DecisionJSON): CaslAppAbility {
  return defineAbilitiesFor({
    uid: json.uid ?? '',
    email: json.email ?? 'access@local',
    role: [...json.roles],
  });
}

function canOnResource(
  ability: CaslAppAbility,
  action: Action,
  subject: Subject,
  resource: Record<string, unknown>,
): boolean {
  return ability.can(action, caslSubject(subject, resource));
}

export function httpStatusFor(kind: AccessReasonKind): 200 | 401 | 403 | 503 {
  if (kind === 'ok') {
    return 200;
  }
  if (kind === 'unavailable') {
    return 503;
  }
  if (kind === 'invalid-credential' || kind === 'no-email' || kind === 'unverified-email') {
    return 401;
  }
  return 403;
}

export function keepCookieFor(kind: AccessReasonKind): boolean {
  return kind === 'ok' || kind === 'unavailable' || kind === 'forbidden';
}

export function makeDecision(partial: {
  uid: string | null;
  email: string | null;
  emailKey: string | null;
  existence: DecisionJSON['existence'];
  reason: AccessReason;
  roles: UserRole[];
  fallbackHit?: boolean;
  issuedAt?: number;
}): Decision {
  const rules = partial.reason.kind === 'ok' ? packRules(partial.roles) : [];
  const issuedAt = partial.issuedAt ?? Date.now();
  const fallbackHit = partial.fallbackHit ?? false;
  const json: DecisionJSON = {
    v: 1,
    uid: partial.uid,
    email: partial.email,
    emailKey: partial.emailKey,
    existence: partial.existence,
    reason: partial.reason,
    roles: partial.reason.kind === 'ok' ? [...partial.roles] : [],
    rules,
    issuedAt,
    fallbackHit,
  };

  let cachedAbility: CaslAppAbility | undefined;

  const decision: Decision = {
    uid: json.uid,
    email: json.email,
    emailKey: json.emailKey,
    existence: json.existence,
    reason: json.reason,
    roles: json.roles,
    issuedAt,
    fallbackHit,
    can(action, subject, resource) {
      if (json.reason.kind !== 'ok') {
        return false;
      }
      if (resource !== undefined) {
        if (!cachedAbility) {
          cachedAbility = caslAbilityForDecision(json);
        }
        return canOnResource(cachedAbility, action, subject, resource);
      }
      return json.rules.some(
        (rule) =>
          (rule.action === 'manage' && rule.subject === 'all') ||
          (rule.action === action && rule.subject === subject) ||
          (rule.action === 'manage' && rule.subject === subject),
      );
    },
    explain(action, subject, resource) {
      if (json.reason.kind !== 'ok') {
        return json.reason;
      }
      if (decision.can(action, subject, resource)) {
        return { kind: 'ok' };
      }
      return { kind: 'forbidden', action, subject };
    },
    gate(need, resource) {
      if (json.reason.kind !== 'ok') {
        return {
          status: httpStatusFor(json.reason.kind) as 401 | 403 | 503,
          reason: json.reason,
          keepCookie: keepCookieFor(json.reason.kind),
        };
      }
      if (need && !decision.can(need.action, need.subject, resource)) {
        const reason = decision.explain(need.action, need.subject, resource);
        return { status: 403, reason, keepCookie: true };
      }
      return null;
    },
    toJSON() {
      return { ...json, rules: [...json.rules], roles: [...json.roles] };
    },
  };
  return decision;
}

export function decisionFromJson(json: DecisionJSON): Decision {
  return makeDecision({
    uid: json.uid,
    email: json.email,
    emailKey: json.emailKey,
    existence: json.existence,
    reason: json.reason,
    roles: [...json.roles],
    fallbackHit: json.fallbackHit,
    issuedAt: json.issuedAt,
  });
}

export function forbiddenAbility(action: Action, subject: Subject): HttpFailure {
  return {
    status: 403,
    reason: { kind: 'forbidden', action, subject },
    keepCookie: true,
  };
}

export function unavailableFailure(message?: string): HttpFailure {
  return {
    status: 503,
    reason: { kind: 'unavailable', message },
    keepCookie: true,
  };
}
